import {
  Competitors,
  CompetitorCoverage,
  CompetitionMonthlyPrices,
  Devices,
  Device,
  OwnedDevices,
  QueryString,
  DeviceQuantities,
} from './models';
import queryString, { ParseOptions } from 'query-string';
import { Location, History } from 'history';

export const filterObjectZeroValues = (obj: QueryString): QueryString => {
  return Object.keys(obj).reduce((q, n) => {
    // obj[n] can be either a number or string
    if (!!+obj[n] || typeof obj[n] === 'string' || (obj[n] as any) instanceof String) q[n] = obj[n];
    return q;
  }, {});
};

export const devicesTotalValue = (deviceQuantities: DeviceQuantities, devices: Devices): number => {
  return Object.keys(deviceQuantities).reduce((t, name) => {
    if (devices[name]) t += deviceQuantities[name] * devices[name].averagePrice;
    return t;
  }, 0);
};

export const formatDollars = (value: number, minimumFractionDigits: number = 2): string => {
  const formatter: Intl.NumberFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
  });
  return formatter.format(value);
};

export const buildQuery = (params: { [key: string]: string }, devices: Devices): QueryString =>
  Object.keys(devices).reduce((q, n) => ({ ...q, [n]: parseInt(params[n]) || 0 }), {});

export const buildSplitQuery = (
  params: { [key: string]: string },
  devices: Devices
): { Devices: DeviceQuantities; NonDevices: QueryString } => {
  return {
    Devices: Object.keys(devices).reduce((q, n) => ({ ...q, [n]: parseInt(params[n]) || 0 }), {}),
    NonDevices: Object.keys(params)
      .filter((f) => Object.keys(devices).indexOf(f) < 0)
      .reduce((q, n) => ({ ...q, [n]: params[n] }), {}),
  };
};

export const deviceNameSort = (devices: Devices): Array<string> =>
  Object.keys(devices).sort((a, b) => (devices[a].index < devices[b].index ? -1 : 1));

export const updateQueryString = (history: History, query: QueryString, push = false): void => {
  const search = queryString.stringify(filterObjectZeroValues(query));
  if (history) {
    if (push) {
      history.push({ search, hash: history.location.hash });
    } else {
      history.replace({ search, hash: history.location.hash });
    }
  }
};

export const getQueryString = (location: Location): any =>
  queryString.parse((location || {}).search, { ignoreQueryPrefix: true } as ParseOptions);

export const calculateCompetitionMonthlyPrices = (
  competitors: Competitors,
  devices: Devices,
  ownedDevices?: OwnedDevices
): CompetitionMonthlyPrices => {
  const competitionMonthlyPrices = {};

  if (!ownedDevices) {
    return competitionMonthlyPrices;
  }

  for (let competitor in competitors) {
    competitionMonthlyPrices[competitor] = 0;
  }

  for (let ownedDeviceName in ownedDevices) {
    if (devices.hasOwnProperty(ownedDeviceName)) {
      const ownedDevice: Device = devices[ownedDeviceName];

      if (typeof ownedDevice !== 'object' || typeof ownedDevice.competitorCoverage !== 'object') {
        throw new Error(`Invalid device "${ownedDeviceName}."`);
      }

      const quantityOwned: number = parseInt(ownedDevices[ownedDeviceName]);
      const competitorCoverage: CompetitorCoverage = ownedDevice.competitorCoverage;

      for (let competitor in competitorCoverage) {
        if (!competitionMonthlyPrices.hasOwnProperty(competitor)) {
          throw new Error(`Invalid competitor "${competitor}".`);
        }

        const price: number = competitorCoverage[competitor].price;
        const years: number = competitorCoverage[competitor].years;

        if (price === 0 || years === 0) {
          continue;
        } else if (typeof price !== 'number' || price < 0 || typeof years !== 'number' || years < 0) {
          throw new Error('Invalid price or years.');
        }

        competitionMonthlyPrices[competitor] += (price * quantityOwned) / years / 12;
      }
    } else {
      console.error(new Error(`Invalid device selection "${ownedDeviceName}."`));
    }
  }

  return competitionMonthlyPrices;
};

export const calculateMonthlySavings = (competitionMonthlyPrices: CompetitionMonthlyPrices) =>
  Math.max(...Object.values(competitionMonthlyPrices)) - 20;



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/calculator/utils.ts